import React, { useCallback, useMemo, useState } from 'react';
import { Percent, Pencil, Plus, Trash2, X } from 'lucide-react';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import { getThemeColor } from '../../config/appConfig';
import { useToast } from '../../contexts/ToastContext';
import { useFetchOnceByKey } from '../../hooks/useMountOnce';
import apiService from '../../utils/apiService';
import '../../styles/module-shared.css';
import './sub-commission.css';

const THEME = getThemeColor('primary');

const pick = (obj, ...keys) => {
  for (const key of keys) {
    if (obj?.[key] != null && obj[key] !== '') return obj[key];
  }
  return undefined;
};

const toBool = (value, fallback = true) => {
  if (typeof value === 'boolean') return value;
  if (value == null) return fallback;
  if (typeof value === 'string') {
    const v = value.toLowerCase();
    if (v === 'true' || v === '1') return true;
    if (v === 'false' || v === '0') return false;
  }
  return Boolean(value);
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  // Ignore default/min SQL dates (e.g. 0001-01-01)
  if (d.getFullYear() < 1970) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatPct = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}%`;
};

export const normalizeSubCommissions = (payload) => {
  const list = Array.isArray(payload)
    ? payload
    : payload?.result ?? payload?.data ?? payload?.Result ?? [];
  if (!Array.isArray(list)) return [];

  return list
    .map((row) => {
      const id = Number(pick(row, 'affiliateSubCommissionId', 'AffiliateSubCommissionId', 'id'));
      const percentage = Number(pick(row, 'percentage', 'Percentage'));
      if (!Number.isFinite(id) || !Number.isFinite(percentage)) return null;
      return {
        id,
        percentage,
        isActive: toBool(pick(row, 'isActive', 'IsActive'), true),
        createdDateTime: pick(row, 'createdDateTime', 'CreatedDateTime') || null,
        updatedDateTime: pick(row, 'updatedDateTime', 'UpdatedDateTime') || null,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.percentage - b.percentage);
};

const emptyForm = () => ({ percentage: '', isActive: true });

const SubCommission = () => {
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchList = useCallback(async ({ cancelled } = {}) => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.getAffiliateSubCommissions();
      if (cancelled?.()) return;
      if (!res.success) {
        setRows([]);
        setError(res.error || res.message || 'Failed to load sub commissions');
        return;
      }
      setRows(normalizeSubCommissions(res.data ?? res.result ?? res));
    } catch (err) {
      if (!cancelled?.()) {
        setRows([]);
        setError(err.message || 'Failed to load sub commissions');
      }
    } finally {
      if (!cancelled?.()) setLoading(false);
    }
  }, []);

  useFetchOnceByKey('sub-commission-list', fetchList, true);

  const stats = useMemo(() => {
    const active = rows.filter((r) => r.isActive).length;
    return {
      total: rows.length,
      active,
      inactive: rows.length - active,
    };
  }, [rows]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      percentage: String(row.percentage),
      // Prefer explicit true; missing/undefined treated as active for safer edits
      isActive: row.isActive !== false,
    });
    setFormError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
    setForm(emptyForm());
    setFormError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const pct = Number(form.percentage);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      setFormError('Enter a percentage between 0 and 100');
      return;
    }

    setSaving(true);
    setFormError('');
    try {
      const res = editing
        ? await apiService.updateAffiliateSubCommission({
            affiliateSubCommissionId: editing.id,
            percentage: pct,
            isActive: form.isActive,
          })
        : await apiService.createAffiliateSubCommission({
            percentage: pct,
            isActive: form.isActive,
          });

      if (!res.success) {
        setFormError(res.error || res.message || 'Save failed');
        return;
      }

      showSuccess(res.message || (editing ? 'Updated successfully' : 'Created successfully'));
      setModalOpen(false);
      setEditing(null);
      setForm(emptyForm());
      await fetchList({ cancelled: () => false });
    } catch (err) {
      setFormError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await apiService.deleteAffiliateSubCommission(deleteTarget.id);
      if (!res.success) {
        showError(res.error || res.message || 'Delete failed');
        return;
      }
      showSuccess(res.message || 'Deleted successfully');
      setDeleteTarget(null);
      await fetchList({ cancelled: () => false });
    } catch (err) {
      showError(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="page sc-page" style={{ '--sc-primary': THEME }}>
        <div className="page-inner">
          <div className="module-card">
            <AppLoader color={THEME} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page sc-page" style={{ '--sc-primary': THEME }}>
      <div className="page-inner">
        <div className="sc-header-row">
          <ModulePageHeader
            icon={Percent}
            title="Sub Partner Commission"
            subtitle="Manage commission percentages for sub partner invites"
            iconBg={`linear-gradient(135deg,${THEME},#a52a2a)`}
          />
          <button type="button" className="sc-btn sc-btn--primary" onClick={openCreate}>
            <Plus className="w-4 h-4" />
            Add percentage
          </button>
        </div>

        <ModuleAlert message={error} />

        <section className="sc-stats">
          <article className="sc-stat">
            <strong>{stats.total}</strong>
            <span>Total</span>
          </article>
          <article className="sc-stat is-active">
            <strong>{stats.active}</strong>
            <span>Active</span>
          </article>
          <article className="sc-stat is-inactive">
            <strong>{stats.inactive}</strong>
            <span>Inactive</span>
          </article>
        </section>

        <section className="module-card sc-card">
          {rows.length === 0 ? (
            <div className="sc-empty">
              <Percent className="sc-empty__icon" />
              <p>No sub commissions yet. Add a percentage to get started.</p>
              <button type="button" className="sc-btn sc-btn--primary" onClick={openCreate}>
                <Plus className="w-4 h-4" />
                Add percentage
              </button>
            </div>
          ) : (
            <div className="sc-table-wrap">
              <table className="sc-table">
                <thead>
                  <tr>
                    <th>Percentage</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Updated</th>
                    <th className="sc-table__actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <span className="sc-pct">{formatPct(row.percentage)}</span>
                      </td>
                      <td>
                        <span className={`sc-status ${row.isActive ? 'is-active' : 'is-inactive'}`}>
                          {row.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{formatDate(row.createdDateTime)}</td>
                      <td>{formatDate(row.updatedDateTime)}</td>
                      <td className="sc-table__actions">
                        <button
                          type="button"
                          className="sc-icon-btn"
                          onClick={() => openEdit(row)}
                          aria-label="Edit"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          className="sc-icon-btn is-danger"
                          onClick={() => setDeleteTarget(row)}
                          aria-label="Delete"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {modalOpen ? (
        <div className="sc-modal" role="presentation">
          <button type="button" className="sc-modal__backdrop" onClick={closeModal} aria-label="Close" />
          <div className="sc-modal__panel" role="dialog" aria-modal="true" aria-labelledby="sc-modal-title">
            <div className="sc-modal__head">
              <h2 id="sc-modal-title">{editing ? 'Edit sub commission' : 'Add sub commission'}</h2>
              <button type="button" className="sc-icon-btn" onClick={closeModal} disabled={saving} aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleSave}>
              <div className="sc-modal__body">
                <label className="sc-label" htmlFor="sc-percentage">
                  Percentage
                </label>
                <input
                  id="sc-percentage"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  className="sc-input"
                  value={form.percentage}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, percentage: e.target.value }));
                    if (formError) setFormError('');
                  }}
                  placeholder="e.g. 25"
                  disabled={saving}
                  autoFocus
                  required
                />

                <label className="sc-check">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                    disabled={saving}
                  />
                  <span>Active</span>
                </label>

                {formError ? <span className="sc-form-error">{formError}</span> : null}
              </div>
              <div className="sc-modal__foot">
                <button type="button" className="sc-btn sc-btn--ghost" onClick={closeModal} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="sc-btn sc-btn--primary" disabled={saving}>
                  {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="sc-modal" role="presentation">
          <button
            type="button"
            className="sc-modal__backdrop"
            onClick={() => !deleting && setDeleteTarget(null)}
            aria-label="Close"
          />
          <div className="sc-modal__panel sc-modal__panel--sm" role="dialog" aria-modal="true">
            <div className="sc-modal__head">
              <h2>Delete sub commission?</h2>
              <button
                type="button"
                className="sc-icon-btn"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="sc-modal__body">
              <p className="sc-delete-copy">
                Soft-delete <strong>{formatPct(deleteTarget.percentage)}</strong>? It will no longer appear in invite
                options.
              </p>
            </div>
            <div className="sc-modal__foot">
              <button
                type="button"
                className="sc-btn sc-btn--ghost"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button type="button" className="sc-btn sc-btn--danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SubCommission;
